import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Calendar, MapPin, Globe, Users, Search, Filter, ChevronRight,
  Building2, Ticket, ArrowRight, Clock, Tag
} from 'lucide-react';
import axios from 'axios';
import { MyHackathonsSection } from '../components/events/MyHackathonsSection';

const API = process.env.REACT_APP_BACKEND_URL;

const CodeArenaEventsPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchEvents();
    fetchCategories();
  }, [search, selectedCategory, selectedType, page]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedType) params.append('event_type', selectedType);
      params.append('page', page);
      params.append('limit', 12);
      const res = await axios.get(`${API}/api/public/events?${params}`);
      setEvents(res.data.events || []);
      setTotalPages(res.data.pages || 1);
    } catch { } finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/public/event-categories`);
      setCategories(res.data.categories || []);
    } catch { }
  };

  const formatDate = (d) => {
    if (!d) return 'TBD';
    try { return new Date(d).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return d; }
  };

  return (
    <div className="min-h-screen bg-[#f3f2ef]" data-testid="codearena-events-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex items-center gap-2 text-sm text-[#666] mb-2">
            <Link to="/codearena" className="hover:text-[#0a66c2]">EvalentX Arena</Link>
            <ChevronRight size={14} />
            <span className="text-[#191919] font-medium">Events</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#191919] mb-2">Events & Opportunities</h1>
          <p className="text-[#666] text-base">Discover tech events, hackathons, workshops, and meetups hosted by leading companies.</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <MyHackathonsSection />
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2]"
              placeholder="Search events..." data-testid="events-search" />
          </div>
          <select value={selectedCategory} onChange={e => { setSelectedCategory(e.target.value); setPage(1); }}
            className="px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-[#666] focus:outline-none focus:border-[#0a66c2]" data-testid="events-category-filter">
            <option value="">All Categories</option>
            {categories.map(c => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
          </select>
          <select value={selectedType} onChange={e => { setSelectedType(e.target.value); setPage(1); }}
            className="px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-[#666] focus:outline-none focus:border-[#0a66c2]" data-testid="events-type-filter">
            <option value="">All Types</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
          </select>
        </div>

        {/* Category Pills */}
        {categories.length > 0 && (
          <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
            <button onClick={() => { setSelectedCategory(''); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${!selectedCategory ? 'bg-[#0a66c2] text-white' : 'bg-white text-[#666] border border-gray-200 hover:bg-gray-50'}`}>
              All
            </button>
            {categories.slice(0, 10).map(c => (
              <button key={c.name} onClick={() => { setSelectedCategory(c.name); setPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${selectedCategory === c.name ? 'bg-[#0a66c2] text-white' : 'bg-white text-[#666] border border-gray-200 hover:bg-gray-50'}`}>
                {c.name}
              </button>
            ))}
          </div>
        )}

        {/* Events Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#0a66c2] border-t-transparent rounded-full animate-spin" /></div>
        ) : events.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
            <Calendar className="mx-auto text-gray-300 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-[#191919] mb-2">No events found</h3>
            <p className="text-sm text-[#666]">{search ? 'Try a different search term' : 'Check back soon for upcoming events'}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {events.map(event => (
              <Link to={`/events/${event.id}`} key={event.id}
                className="group bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all overflow-hidden" data-testid={`event-listing-${event.id}`}>
                {/* Cover */}
                <div className="h-40 bg-gradient-to-br from-slate-100 to-slate-50 relative overflow-hidden">
                  {event.cover_image ? (
                    <img src={event.cover_image} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
                      <Calendar size={40} className="text-blue-300" />
                    </div>
                  )}
                  <div className="absolute top-3 left-3 flex gap-1.5">
                    {event.is_paid ? (
                      <span className="px-2 py-0.5 bg-purple-600 text-white rounded-md text-xs font-medium">&#8377;{event.price}</span>
                    ) : (
                      <span className="px-2 py-0.5 bg-emerald-600 text-white rounded-md text-xs font-medium">Free</span>
                    )}
                    <span className="px-2 py-0.5 bg-black/60 text-white rounded-md text-xs font-medium capitalize">{event.event_type}</span>
                  </div>
                </div>
                {/* Content */}
                <div className="p-4">
                  <div className="flex gap-1.5 mb-2 flex-wrap">
                    {event.categories?.slice(0, 2).map((c, i) => (
                      <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-medium">{c}</span>
                    ))}
                  </div>
                  <h3 className="font-semibold text-[#191919] text-base mb-2 group-hover:text-[#0a66c2] transition-colors line-clamp-2">{event.title}</h3>
                  <div className="space-y-1.5 text-xs text-[#666]">
                    <div className="flex items-center gap-1.5"><Calendar size={12} />{formatDate(event.event_date)}</div>
                    <div className="flex items-center gap-1.5">{event.event_type === 'offline' ? <MapPin size={12} /> : <Globe size={12} />}{event.event_type === 'offline' ? event.venue || 'Venue TBD' : 'Online Event'}</div>
                    <div className="flex items-center gap-1.5"><Building2 size={12} />{event.company_name}</div>
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                    <span className="text-xs text-[#666] flex items-center gap-1"><Users size={12} />{event.registration_count || 0} registered</span>
                    <span className="text-xs font-medium text-[#0a66c2] flex items-center gap-1 group-hover:gap-2 transition-all">View <ArrowRight size={12} /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)}
                className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${page === p ? 'bg-[#0a66c2] text-white' : 'bg-white text-[#666] border border-gray-200 hover:bg-gray-50'}`}>
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeArenaEventsPage;
